import { BaseMicroAppPlugin } from '../core/BaseMicroAppPlugin'
import { IMicroAppData } from '../core/types'
import Vue from 'vue'

export interface IMicroAppStore {
  current: {
    apps: Map<string, IMicroAppData & { count?: number }>;
  };
  history: IMicroAppData[];
}

export class StateManagerPlugin extends BaseMicroAppPlugin {
  private store: IMicroAppStore;
  private readonly MAX_HISTORY_LENGTH = 100;

  constructor() {
    super('state-manager', 'Micro App State Manager')
    this.store = Vue.observable<IMicroAppStore>({
      current: {
        apps: new Map()
      },
      history: []
    })
  }

  onMount(data: IMicroAppData): void {
    if (!this.enabled) return
    const key = `${data.appName}-${data.componentName}`;
    const existingApp = this.store.current.apps.get(key);
    const mountCount = (existingApp?.mountCount || 0) + 1;

    // Store the instanceIds in an array
    const instanceIds = existingApp?.instanceIds || [];
    instanceIds.push(data.instanceId);

    const newApps = new Map(this.store.current.apps);
    newApps.set(key, { ...data, mountCount, instanceIds });
    this.store.current.apps = newApps;

    this.addToHistory({ ...data, action: 'mount' });
  }

  onUnmount(data: IMicroAppData): void {
    if (!this.enabled) return
    const key = `${data.appName}-${data.componentName}`;
    const existingApp = this.store.current.apps.get(key);
    if (!existingApp) return;

    // Remove the instanceId from the array
    const instanceIds = existingApp.instanceIds?.filter(id => id !== data.instanceId) || [];

    if (existingApp.mountCount > 1) {
      const newApps = new Map(this.store.current.apps);
      newApps.set(key, {
        ...existingApp,
        mountCount: (existingApp.mountCount ?? 1) - 1,
        instanceIds
      });
      this.store.current.apps = newApps;
    } else {
      this.store.current.apps.delete(key);
    }

    this.addToHistory({ ...data, action: 'unmount' })
  }

  resetCounts(): void {
    const newApps = new Map();
    this.store.current.apps.forEach((app, key) => {
      newApps.set(key, { ...app, mountCount: 0 });
    });
    this.store.current.apps = newApps;
  }

  private addToHistory(data: IMicroAppData): void {
    this.store.history.unshift(data)
    if (this.store.history.length > this.MAX_HISTORY_LENGTH) {
      this.store.history = this.store.history.slice(0, this.MAX_HISTORY_LENGTH)
    }
  }

  getActiveApps(): IMicroAppData[] {
    return Array.from(this.store.current.apps.values())
  }

  getHistory(): IMicroAppData[] {
    return this.store.history
  }

  getStore(): IMicroAppStore {
    return this.store
  }
}
